/**
 * Majority judgment, exactly as Balinski and Laraki define it.
 *
 * This is ionoff's single ranking rule. Every place that turns a set of
 * grades into one grade, or stacks two candidates, must go through this
 * file. It has no dependencies and is duplicated VERBATIM in
 *   functions/src/majorityJudgment.ts
 *   ionoff/src/services/majorityJudgment.ts
 * scripts/test-majority-judgment.js fails if the two copies differ.
 *
 * The rule
 * --------
 * Grades run A (best) to F (worst). For a candidate with n grades:
 *
 * 1. MAJORITY GRADE. The highest grade that an absolute majority of judges
 *    (strictly more than half) gives *or better*. With the grades sorted
 *    best-first this is the grade at index floor(n/2). When n is even that
 *    is the LOWER of the two middle grades — not a blend of them. Two
 *    judges giving A and C yield C, because only one of two (not a
 *    majority) gave A or better, while both gave C or better.
 *
 * 2. MAJORITY RANKING. Two candidates are compared by their MAJORITY
 *    GAUGES (p, α, q): α is the majority grade, p the share of grades
 *    strictly above it, q the share strictly below. A higher α wins. At the
 *    same α, α+ (p > q) beats α (p = q) beats α− (p < q); between two α+
 *    the larger p wins; between two α− the smaller q wins.
 *
 *    If the gauges tie, one copy of α is removed from each candidate and
 *    the reduced candidates are compared the same way, repeatedly. For two
 *    electorates of the same size this reproduces Balinski and Laraki's
 *    MAJORITY VALUE — the sequence of successive majority grades — step for
 *    step, because their theorem says the gauge never contradicts the
 *    majority value where it decides. For electorates of different sizes
 *    it is the direct generalisation, with shares in place of counts.
 *
 *    A candidate that runs out of grades while still tied ranks below the
 *    one with grades left: with every step equal, the larger electorate is
 *    the stronger evidence. This keeps the ranking a total preorder, so
 *    sorting cannot depend on input order.
 *
 * 3. Two candidates are tied only when the procedure exhausts both at once,
 *    i.e. their grade multisets are identical. Callers may then use
 *    non-grade information (earlier timestamp), which is outside majority
 *    judgment and must never override steps 1–2.
 *
 * The comparator is a lexicographic order over a per-candidate key
 * sequence, so it is antisymmetric and transitive by construction;
 * scripts/test-majority-judgment.js checks both exhaustively.
 *
 * Why this matters: the previous implementation blended an even split into
 * "A/C" and ranked that above "B". Majority judgment ranks BB above AC —
 * a candidate two judges call good beats one that one judge loved and one
 * found middling. Rewarding the consensus candidate over the polarising one
 * is the entire point of the method.
 */

export type Letter = "A" | "B" | "C" | "D" | "F";

/** Best first. Index in this array is the only ordering the rule uses. */
export const GRADE_SCALE: readonly Letter[] = ["A", "B", "C", "D", "F"];

export interface GradeCounts {
  A: number;
  B: number;
  C: number;
  D: number;
  F: number;
}

export type GaugeSign = "+" | "" | "-";

export interface MajorityGauge {
  grade: Letter;
  /** Number of grades strictly better than `grade`. */
  p: number;
  /** Number of grades strictly worse than `grade`. */
  q: number;
  n: number;
  sign: GaugeSign;
}

export function emptyCounts(): GradeCounts {
  return {A: 0, B: 0, C: 0, D: 0, F: 0};
}

export function isLetter(g: unknown): g is Letter {
  return g === "A" || g === "B" || g === "C" || g === "D" || g === "F";
}

function scaleIndex(g: Letter): number {
  return GRADE_SCALE.indexOf(g);
}

/**
 * Coerce a raw grade string to a letter, or null if it is not a grade.
 * Tolerates whitespace, lower case, "+"/"-" suffixes, and the legacy blend
 * form "X/Y". A blend was only ever produced from an even split whose two
 * middle grades were X and Y; the majority grade of that same set is the
 * lower of the two, so "A/C" resolves to C. That is exact, not a guess.
 */
export function normalizeGrade(raw: unknown): Letter | null {
  if (typeof raw !== "string") return null;
  const s = raw.trim().toUpperCase();
  if (!s) return null;
  if (s.includes("/")) {
    // Only a true blend "X/Y" of two letters is accepted; "N/A" is not a grade.
    const parts = s.split("/").map((x) => x.trim().replace(/[+-]$/, ""));
    if (parts.length !== 2 || !parts.every(isLetter)) return null;
    const [x, y] = parts as Letter[];
    return scaleIndex(x) >= scaleIndex(y) ? x : y;
  }
  const bare = s.replace(/[+-]$/, "");
  return isLetter(bare) ? bare : null;
}

export function countsFromGrades(grades: readonly unknown[]): GradeCounts {
  const c = emptyCounts();
  for (const g of grades) {
    const l = normalizeGrade(g);
    if (l) c[l]++;
  }
  return c;
}

export function totalCount(c: GradeCounts): number {
  return (c.A | 0) + (c.B | 0) + (c.C | 0) + (c.D | 0) + (c.F | 0);
}

/** The majority grade, or null when there are no grades. */
export function majorityGrade(c: GradeCounts): Letter | null {
  const n = totalCount(c);
  if (n <= 0) return null;
  let cumulative = 0;
  for (const g of GRADE_SCALE) {
    cumulative += c[g] | 0;
    // Strictly more than half give this grade or better.
    if (cumulative * 2 > n) return g;
  }
  return "F";
}

export function majorityGauge(c: GradeCounts): MajorityGauge | null {
  const grade = majorityGrade(c);
  if (!grade) return null;
  const n = totalCount(c);
  let p = 0;
  let q = 0;
  for (const g of GRADE_SCALE) {
    if (scaleIndex(g) < scaleIndex(grade)) p += c[g] | 0;
    else if (scaleIndex(g) > scaleIndex(grade)) q += c[g] | 0;
  }
  const sign: GaugeSign = p > q ? "+" : p < q ? "-" : "";
  return {grade, p, q, n, sign};
}

/**
 * The majority value: successive majority grades as one copy of each is
 * removed. Length equals the number of grades.
 */
export function majorityValue(c: GradeCounts): Letter[] {
  const work: GradeCounts = {...c};
  const out: Letter[] = [];
  for (;;) {
    const g = majorityGrade(work);
    if (!g) break;
    out.push(g);
    work[g]--;
  }
  return out;
}

/**
 * Stack two candidates. Negative means `a` ranks higher, positive means
 * `b` ranks higher, zero is a genuine tie under majority judgment. Suitable
 * as an Array.prototype.sort comparator for best-first order.
 */
export function compareMajorityJudgment(a: GradeCounts, b: GradeCounts): number {
  const wa: GradeCounts = {...a};
  const wb: GradeCounts = {...b};
  const rank = (s: GaugeSign) => (s === "+" ? 0 : s === "" ? 1 : 2);

  for (;;) {
    const A = majorityGauge(wa);
    const B = majorityGauge(wb);
    if (!A && !B) return 0; // exhausted together: identical multisets
    if (!A) return 1; // a ran out first while tied: b has more evidence
    if (!B) return -1;

    const d = scaleIndex(A.grade) - scaleIndex(B.grade);
    if (d !== 0) return d;

    const ds = rank(A.sign) - rank(B.sign);
    if (ds !== 0) return ds;

    // Shares compared without floating point: p_a/n_a against p_b/n_b.
    if (A.sign === "+") {
      const dp = B.p * A.n - A.p * B.n; // larger share above ranks higher
      if (dp !== 0) return dp;
    } else if (A.sign === "-") {
      const dq = A.q * B.n - B.q * A.n; // smaller share below ranks higher
      if (dq !== 0) return dq;
    }

    // Gauges tie: remove one majority grade from each and look again.
    wa[A.grade]--;
    wb[B.grade]--;
  }
}

/** Display form, e.g. "B+", "B", "B-". Empty string when there are no grades. */
export function formatMajorityGrade(c: GradeCounts): string {
  const g = majorityGauge(c);
  return g ? `${g.grade}${g.sign}` : "";
}
